import { apiService } from '../../services/api';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Lightbulb, Check, X, ThumbsUp, ThumbsDown, Sparkles, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Suggestion {
  id: string;
  suggestionType: string;
  content: string;
  context?: {
    reasoning?: string;
    confidence?: number;
  };
  priority: string;
  status: string;
  createdAt: string;
}

interface SmartSuggestionsProps {
  projectId: number;
  currentText?: string;
  onSuggestionApplied?: (suggestion: Suggestion) => void;
}

export function SmartSuggestions({ projectId, currentText, onSuggestionApplied }: SmartSuggestionsProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('general');
  const { toast } = useToast();

  const suggestionTypes = [
    { value: 'general', label: 'General Writing' },
    { value: 'character_voice', label: 'Character Voice' },
    { value: 'plot_consistency', label: 'Plot Consistency' },
    { value: 'world_building', label: 'World Building' },
    { value: 'style_improvement', label: 'Style Improvement' }
  ];

  useEffect(() => {
    loadSuggestions();
  }, [projectId]);

  const loadSuggestions = async () => {
    setIsLoading(true);
    try {
      const data = await apiService.get(`/api/ai/suggestions?projectId=${projectId}&status=pending`);
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateSuggestions = async () => {
    if (!currentText || currentText.trim().length < 10) {
      toast({
        title: "Need Content",
        description: "Please select some text or write content to get AI suggestions",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    try {
      const data = await apiService.post('/api/ai/suggestions/generate', {
        projectId,
        suggestionType: selectedType,
        currentText: currentText.substring(0, 2000) // Limit text length
      });
      toast({
        title: "Suggestions Generated",
        description: `Generated ${data.suggestions.length} AI suggestions`
      });
      loadSuggestions();
    } catch (error) {
      console.error('Error generating suggestions:', error);
      toast({
        title: "Error",
        description: "Failed to generate suggestions",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const applySuggestion = async (suggestion: Suggestion, feedback?: number) => {
    try {
      await apiService.put(`/api/ai/suggestions/${suggestion.id}/apply`, { feedback });
      toast({
        title: "Suggestion Applied",
        description: "AI suggestion has been applied"
      });
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      onSuggestionApplied?.(suggestion);
    } catch (error) {
      console.error('Error applying suggestion:', error);
      toast({
        title: "Error",
        description: "Failed to apply suggestion",
        variant: "destructive"
      });
    }
  };

  const dismissSuggestion = async (suggestion: Suggestion, feedback?: number) => {
    try {
      await apiService.delete(`/api/ai/suggestions/${suggestion.id}/dismiss`, { data: { feedback } });
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      toast({
        title: "Suggestion Dismissed",
        description: "AI suggestion has been dismissed"
      });
    } catch (error) {
      console.error('Error dismissing suggestion:', error);
      toast({
        title: "Error",
        description: "Failed to dismiss suggestion",
        variant: "destructive"
      });
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <AlertTriangle className="h-3 w-3" />;
      case 'medium': return <Lightbulb className="h-3 w-3" />;
      case 'low': return <Sparkles className="h-3 w-3" />;
      default: return <Lightbulb className="h-3 w-3" />;
    }
  };

  const formatSuggestionType = (type: string) => {
    return suggestionTypes.find(t => t.value === type)?.label || type;
  };

  return (
    <Card>
      <CardHeader className="space-y-4">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <Lightbulb className="h-4 w-4" />
          AI Writing Suggestions
        </CardTitle>
        
        <div className="flex gap-2">
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Suggestion type" />
            </SelectTrigger>
            <SelectContent>
              {suggestionTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            onClick={generateSuggestions}
            disabled={isGenerating || !currentText}
            size="sm"
          >
            {isGenerating ? (
              <Sparkles className="h-3 w-3 animate-pulse" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            Generate
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="text-center py-4 text-muted-foreground">
            Loading suggestions...
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-4 space-y-2">
            <Lightbulb className="h-8 w-8 mx-auto text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              No AI suggestions available
            </div>
            <div className="text-xs text-muted-foreground">
              Write some content and click "Generate" to get AI suggestions
            </div>
          </div>
        ) : (
          <ScrollArea className="h-64">
            <div className="space-y-3">
              {suggestions.map(suggestion => (
                <div key={suggestion.id} className="border rounded p-3 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={getPriorityColor(suggestion.priority)}
                          className="text-xs flex items-center gap-1"
                        >
                          {getPriorityIcon(suggestion.priority)}
                          {suggestion.priority}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {formatSuggestionType(suggestion.suggestionType)}
                        </Badge>
                      </div>
                      
                      <div className="text-sm font-medium">
                        {suggestion.content}
                      </div>
                      
                      {suggestion.context?.reasoning && (
                        <div className="text-xs text-muted-foreground">
                          {suggestion.context.reasoning}
                        </div>
                      )}
                      
                      {suggestion.context?.confidence && (
                        <div className="text-xs text-muted-foreground">
                          Confidence: {Math.round(suggestion.context.confidence * 100)}%
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => applySuggestion(suggestion, 5)}
                        className="h-7 px-2"
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => dismissSuggestion(suggestion, 2)}
                        className="h-7 px-2"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => applySuggestion(suggestion, 4)}
                        className="h-7 px-2"
                      >
                        <ThumbsUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => dismissSuggestion(suggestion, 1)}
                        className="h-7 px-2"
                      >
                        <ThumbsDown className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}